const BELOW_THRESHOLD_COUNT = 5;
const BELOW_THRESHOLD_RATE = 5.0;

export function isBelowStatisticalThreshold(
  count: number,
  rate: number
): boolean {
  return count === 0 || count < BELOW_THRESHOLD_COUNT || rate < (BELOW_THRESHOLD_RATE / 1000);
}

export function formatBurglaryCount(count: number): string {
  if (count === 0 || count < BELOW_THRESHOLD_COUNT) {
    return '< 5';
  }
  return count.toString();
}

export function formatBurglaryRate(rate: number): string {
  if (rate === 0 || rate < (BELOW_THRESHOLD_RATE / 1000)) {
    return '< 5';
  }
  return rate.toFixed(2);
}

export function formatBurglaryRateWithUnit(rate: number): string {
  if (rate === 0 || rate < (BELOW_THRESHOLD_RATE / 1000)) {
    return '< 5‰';
  }
  return `${rate.toFixed(2)}‰`;
}

export function getBurglaryCountLabel(count: number, year: string | number): string {
  if (count === 0 || count < BELOW_THRESHOLD_COUNT) {
    return `Cambriolages en ${year}`;
  }
  return `Cambriolages en ${year}`;
}

export function getBurglaryRateLabel(rate: number): string {
  if (rate === 0 || rate < (BELOW_THRESHOLD_RATE / 1000)) {
    return 'Taux de cambriolages';
  }
  return 'Pour 1000 logements';
}

export function formatBurglaryMessage(
  count: number,
  rate: number,
  year: string | number,
  nationalRate: number,
  riskLevel: 'excellent' | 'good' | 'average' | 'warning' | 'high'
): string {
  const isBelowThreshold = isBelowStatisticalThreshold(count, rate);

  if (isBelowThreshold) {
    switch (riskLevel) {
      case 'excellent':
        return `Votre commune enregistre un taux de cambriolages inférieur au seuil de diffusion du Ministère de l'Intérieur (< 5 pour 1000 logements). C'est un secteur extrêmement sûr, bien en dessous de la moyenne nationale (${nationalRate.toFixed(2)}‰).`;

      case 'good':
        return `Votre commune bénéficie d'un taux de cambriolages inférieur au seuil de diffusion (< 5‰), ce qui témoigne d'une excellente sécurité locale, nettement inférieure à la moyenne nationale.`;

      default:
        return `Les données de cambriolages de votre commune sont inférieures au seuil de diffusion du Ministère de l'Intérieur (< 5 pour 1000 logements), indiquant une très bonne sécurité locale.`;
    }
  }

  const plural = count > 1 ? 's' : '';

  switch (riskLevel) {
    case 'excellent':
      return `Avec ${count} cambriolage${plural} en ${year}, votre commune se situe bien en dessous de la moyenne nationale (${nationalRate.toFixed(2)}‰). C'est un secteur très sûr.`;

    case 'good':
      return `Votre commune enregistre ${count} cambriolage${plural} en ${year}, avec un taux de ${rate.toFixed(2)}‰ en dessous de la moyenne nationale. C'est un secteur sûr.`;

    case 'average':
      return `Votre commune se situe dans la moyenne nationale avec un taux de ${rate.toFixed(2)}‰ pour ${count} cambriolage${plural} en ${year}. Une vigilance standard est recommandée.`;

    case 'warning':
      return `Votre commune connaît un taux de ${rate.toFixed(2)}‰, au-dessus de la moyenne nationale (${nationalRate.toFixed(2)}‰), avec ${count} cambriolage${plural} en ${year}. Une vigilance accrue est conseillée.`;

    case 'high':
      return `Attention : avec ${count} cambriolage${plural} en ${year} (${rate.toFixed(2)}‰), votre commune dépasse significativement la moyenne nationale. Des mesures de protection renforcées sont fortement recommandées.`;

    default:
      return `${count} cambriolage${plural} enregistré${plural} en ${year}.`;
  }
}

export function getStatisticalThresholdTooltip(): string {
  return "Les données précises ne sont pas diffusées par le Ministère de l'Intérieur lorsque le nombre de cambriolages est inférieur à 5, conformément au secret statistique. Cela indique une excellente sécurité locale.";
}
